import React, { useState, useEffect, useMemo } from "react";
import { CommonTable } from "../../components/CommonTable";
import { Button } from "../../components/ui/Button";
import { getCategories, Category } from "../../services/caregory";
import CategoryDetailsDialog from "./CategoryDetails";
import AddCategoryDialog from "./AddCategory";
import { Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hooks/reduxHooks";
import { fetchCategoriesThunk, forceRefreshCategories } from "../../features/categories/categorySlice";

const ManageCategory: React.FC = () => {
    const dispatch = useAppDispatch();
    const { categories, loading, hasFetched } = useAppSelector(state => state.categories);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [showDialog, setShowDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);

    // Filter categories based on search query
    const filteredCategories = useMemo(() => {
        let data = [...categories];
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            data = data.filter(
                c =>
                    c.name.toLowerCase().includes(q) ||
                    c.code.toLowerCase().includes(q) ||
                    (c.subCategory?.some(sub => sub.toLowerCase().includes(q)) ?? false)
            );
        }
        return data;
    }, [categories, searchQuery]);

    // Paginate filtered categories
    const paginatedCategories = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredCategories.slice(start, start + pageSize);
    }, [filteredCategories, page, pageSize]);

    const total = filteredCategories.length;

    // Fetch categories if not already fetched
    useEffect(() => {
        if (!hasFetched) {
            dispatch(fetchCategoriesThunk());
        }
    }, [hasFetched, dispatch]);

    // Refresh categories
    const handleRefresh = () => {
        dispatch(forceRefreshCategories());
    };


    // Table columns
    const columns = [
        {
            id: "srNo",
            header: "Sr. No",
            cell: ({ row }: any) => (page - 1) * pageSize + row.index + 1,
            size: 60,
        },
        {
            id: "code",
            header: "Code",
            accessorKey: "code",
            size: 120,
        },
        {
            id: "subCategory",
            header: "Subcategories",
            accessorKey: "subCategory",
        },
        {
            id: "name",
            header: "Name",
            accessorKey: "name",
            size: 200,
        },
        {
            id: "createdAt",
            header: "Created At",
            accessorKey: "createdAt",
            size: 180,
            cell: ({ row }: any) => {
                const date = new Date(row.original.createdAt);
                return date.toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                });
            },
        },
    ];

    return (
        <div className="w-full no-scrollbar">
            {/* Header with Add Category */}
            <div className="flex justify-end items-center mb-4 mr-1">
                <Button
                    onClick={() => setShowAddDialog(true)}
                    className="flex items-center gap-1"
                    size="sm"
                >
                    <Plus size={14} />
                    <span>Add Category</span>
                </Button>
            </div>

            {/* Categories Table */}
            <div className="bg-white rounded-lg shadow-sm">
                <CommonTable
                    columns={columns}
                    data={paginatedCategories}
                    isLoading={loading}
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    onSortChange={() => { }}
                    onSearchChange={setSearchQuery}
                    searchPlaceholder="Search categories..."
                    enableRowClick={true}
                    onRefresh={handleRefresh} // <-- Add this prop!
                    onRowClick={(row: Category) => {
                        setSelectedCategory(row);
                        setShowDialog(true);
                    }}
                />

                {/* Edit Category Dialog */}
                <CategoryDetailsDialog
                    category={selectedCategory}
                    isOpen={showDialog}
                    onClose={() => setShowDialog(false)}
                    onUpdated={handleRefresh}
                />

                {/* Add Category Dialog */}
                <AddCategoryDialog
                    isOpen={showAddDialog}
                    onClose={() => setShowAddDialog(false)}
                    onAdded={handleRefresh} // refresh table after adding
                />
            </div>
        </div>
    );
};

export default ManageCategory;
